import { SxProps, Theme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import {
  putV1CompaniesCompanyIdLoadsLoadIdApprove,
  PutV1CompaniesCompanyIdLoadsLoadIdApproveData,
  putV1CompaniesCompanyIdLoadsLoadIdUnapprove,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useState } from 'react';

import { Chip } from '~components/Chip/Chip';
import { CopyToClipboard } from '~components/CopyToClipboard/CopyToClipboard';
import useAiReviewDismissal from '~hooks/useAiReview/useAiReviewDismissal';
import { trackLoadsApproved } from '~hooks/useAnalytics/trackApprovals';
import { Load } from '~hooks/useLoad/models';
import { Ticket } from '~hooks/useTickets/models';
import LoadReviewIndicator from '~pages/Approvals/ApprovalsComponents/LoadReviewIndicator';
import ApprovalActionsMenu from '~pages/Approvals/DriverDay/components/ApprovalActionsMenu';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { getApprovalStatus } from '~utils/jobs/job-utils';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

interface ApprovalActionsColumnProps {
  // FIXME @todo This comes from 'Job#order.editable', need to validate if this is actually needed.
  // Reference: src/pages/Approvals/Files/components/LoadsTable/TableRow.tsx
  isOrderEditable: boolean;
  jobId?: string;
  load: Load;
  onAiReviewDismissed: () => void;
  onLoadUpdate: (load: Load) => void;
  sx?: SxProps<Theme>;
  ticket?: Ticket;
}

export default function ApprovalActionsColumn({
  isOrderEditable,
  jobId,
  load,
  onAiReviewDismissed,
  onLoadUpdate,
  sx,
  ticket,
}: ApprovalActionsColumnProps) {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const { isApproved, isApprovable, isUnapprovable } = getApprovalStatus(
    load,
    userStore.getPermissions(),
    isOrderEditable,
  );

  const { dismiss: handleDismissAiReview, undismiss: handleUndoAiReviewDismissal } =
    useAiReviewDismissal({ companyId, load, onChanged: onAiReviewDismissed });

  return (
    <Box
      sx={{
        alignItems: 'flex-end',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        ...sx,
      }}
    >
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}
      >
        <ApprovalActions
          companyId={companyId}
          isApprovable={isApprovable}
          isApproved={isApproved}
          isUnapprovable={isUnapprovable}
          load={load}
          onLoadUpdate={onLoadUpdate}
        />

        <LoadReviewIndicator
          aiReview={load.aiReview}
          canDismiss={isApprovable || isApproved}
          isTicketFlagged={!!ticket?.flagged}
          onDismiss={handleDismissAiReview}
          onUndoDismiss={handleUndoAiReviewDismissal}
        />
      </Box>

      {jobId && (
        <CopyToClipboard
          alertMessageDuration={1000}
          hideTextWhileCopying
          textToCopy={jobId}
          sx={{ maxHeight: '10px' }}
        >
          <Typography
            sx={(theme) => ({ color: theme.brandV2.colors.treadGray2, fontSize: '10px' })}
          >
            {jobId}
          </Typography>
        </CopyToClipboard>
      )}
    </Box>
  );
}

interface ApprovalActionsProps {
  companyId: string;
  isApprovable: boolean;
  isApproved: boolean;
  isUnapprovable: boolean;
  load: Load;
  onLoadUpdate: (load: Load) => void;
}

function ApprovalActions({
  companyId,
  isApprovable: isLoadApprovable,
  isApproved: isLoadApproved,
  isUnapprovable: isLoadUnapprovable,
  load,
  onLoadUpdate,
}: ApprovalActionsProps) {
  const [isUpdating, setIsUpdating] = useState(false);

  const handleApprovalCTAClick = useCallback(
    async (approvalAction: 'approve' | 'unapprove') => {
      setIsUpdating(true);

      const isApprove = approvalAction === 'approve';
      const pathArgs: PutV1CompaniesCompanyIdLoadsLoadIdApproveData['path'] = {
        'company-id': companyId,
        'load-id': load.id,
      };
      const func =
        approvalAction === 'approve'
          ? putV1CompaniesCompanyIdLoadsLoadIdApprove
          : putV1CompaniesCompanyIdLoadsLoadIdUnapprove;

      try {
        const response = await func({ path: pathArgs });
        const jobLoadSummary = Load.parse(response.data.data);
        jobLoadSummary.loadSummary = load.loadSummary;

        onLoadUpdate(jobLoadSummary);

        if (isApprove) {
          trackLoadsApproved([load]);
        }
      } catch (error) {
        connection.handleRequestError(
          error,
          isApprove
            ? t('error_messages.jobs.failed_to_approve_load')
            : t('error_messages.jobs.failed_to_unapprove_load'),
        );
      } finally {
        setIsUpdating(false);
      }
    },
    [companyId, load, onLoadUpdate],
  );

  if (isLoadApproved && isLoadUnapprovable) {
    return (
      <ApprovalActionsMenu
        approved={isLoadApproved}
        isLoading={isUpdating}
        onMenuItemClick={() => handleApprovalCTAClick('unapprove')}
      />
    );
  }

  if (isLoadApprovable) {
    return (
      <Button
        color="secondary"
        disabled={isUpdating}
        onClick={(event) => {
          event.stopPropagation();
          handleApprovalCTAClick('approve');
        }}
        variant="outlined"
        sx={{ fontSize: '12px', minWidth: 0, px: '4px', py: 0, height: '24px' }}
      >
        {t('approvals.approve')}
      </Button>
    );
  }

  return (
    <Chip
      color={isLoadApproved ? 'success' : 'secondary'}
      hideDropdownArrow
      label={isLoadApproved ? t('status.approved') : t('status.pending')}
    />
  );
}
